import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SteelFace, CutLetters } from "./MiniPlayer";
import BrandPlate from "./FinishPicker";

// `members: true` = only for someone with a login (a guest's header is already full with Log in / Get started). `wide: true` = only on a window 1600 px wide or
// more (the header stops growing there; below that it has no room and the Home tab strip carries the link).
const NAV = [
  { to: "/", label: "Home" },
  { to: "/videos", label: "Music Videos" },
  { to: "/about", label: "About" },
  { to: "/genres", label: "Genres" },
  { to: "/playlists", label: "Playlists" },
  { to: "/my-videos", label: "My Videos", members: true, wide: true },
  { to: "/favorites", label: "Favorites" },
  { to: "/recent", label: "Recently Played" },
];

export default function Navbar({ onSearch }) {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-2 sm:gap-3 xl:gap-4">
        {/* The site name, cut out of a steel plate. It goes to the Home page; for a member it opens the list of looks (Steel, Gold, Quartz). */}
        <BrandPlate user={user} ready={ready} />

        <nav className="hidden lg:flex items-center gap-1 ml-2 xl:ml-4 shrink-0">
          {NAV.filter((n) => !n.members || user).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-1.5 xl:px-3 py-2 text-sm font-medium whitespace-nowrap rounded-md hover:text-white hover:bg-white/5 transition-colors ${n.wide ? "max-[1599px]:hidden " : ""}${isActive ? "text-white bg-white/5" : "text-zinc-400"}`
              }>{n.label}</NavLink>
          ))}
        </nav>

        {/* Opens the search popup. A round steel button on phones (guests on a narrow phone use the one at
            the bottom right instead, to leave room for Log in and Get started), a wide box on bigger screens. */}
        {/* (the wrapper does the showing and hiding, because .steel-btn sets its own display) */}
        <div className={`${user ? "flex" : "hidden sm:flex"} md:hidden lg:flex xl:hidden ml-auto`}>
          <button type="button" onClick={onSearch} aria-label="Search" data-testid="nav-search-btn-phone" className="steel-btn w-10 h-10">
            <SteelFace glyph="search" />
          </button>
        </div>
        <button type="button" onClick={onSearch} aria-label="Search" data-testid="nav-search-btn"
          className="hidden md:flex lg:hidden xl:flex ml-auto md:flex-1 md:min-w-[8rem] md:max-w-md items-center justify-start gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-500 hover:border-cyan-400/40 hover:text-zinc-300 text-left">
          <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <span className="truncate">Search songs, artists, moods, genres…</span>
        </button>

        <div className={`flex items-center gap-2 ${user ? "" : "ml-auto sm:ml-0"}`}>
          {user ? (
            <>
              {/* Small steel tags. Premium glows gold; Admin takes the page's light color and goes to the Admin page.
                  (The wrapper does the showing and hiding, because .steel-pill sets its own display.) */}
              {user.is_premium && (
                <div className="hidden sm:flex">
                  <span data-testid="nav-premium-tag" className="steel-plate steel-pill steel-tag steel-gold"><CutLetters text="Premium" /></span>
                </div>
              )}
              {user.role === "admin" && (
                <div className="hidden sm:flex">
                  <Link to="/admin" data-testid="nav-admin-link" className="steel-plate steel-pill steel-tag"><CutLetters text="Admin" /></Link>
                </div>
              )}
              <Link to="/account" data-testid="nav-account-link" aria-label="Account" className="flex items-center gap-2 text-sm">
                <span className="steel-btn w-10 h-10"><SteelFace glyph="user" /></span>
                <span className="hidden 2xl:inline whitespace-nowrap max-w-[12rem] truncate">{user.name || user.email}</span>
              </Link>
              <button data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }} aria-label="Log out" title="Log out" className="steel-btn w-8 h-8">
                <SteelFace glyph="logout" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="steel-plate steel-pill"><CutLetters text="Log in" /></Link>
              <Link to="/register" data-testid="nav-register-link" className="steel-plate steel-pill"><CutLetters text="Get started" /></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
